import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { useUser } from "../context/UserContext";
import { useApi } from "../utils/Api";
import { checkStoreStatus } from "../utils/storeHours";

// Same look as the order-tracking page
const card =
  "bg-gradient-to-b from-[#161616] to-[#0d0d0d] border border-white/10 rounded-2xl p-6 shadow-[0_10px_30px_rgba(0,0,0,0.35)]";
const primaryBtn =
  "flex w-full items-center justify-center gap-2 rounded-full bg-brand-orange px-5 py-3.5 font-bold text-[15px] text-black no-underline transition hover:opacity-90";
const secondaryBtn =
  "flex w-full items-center justify-center gap-2 rounded-full border-2 border-brand-orange/60 bg-brand-orange/10 px-5 py-3 font-bold text-[15px] text-white no-underline transition hover:bg-brand-orange/20";
function PaymentSuccess() {
  const location = useLocation();
  const navigate = useNavigate();
  const { clearCart } = useCart();
  const { user } = useUser();
  const { apiFetch } = useApi();

  // "checking" -> waiting for Stripe's confirmation, "paid", "failed" (expired / not paid)
  const [state, setState] = useState("checking");
  const [orderId, setOrderId] = useState(null);
  const [total, setTotal] = useState(null);
  const [trackingEncryptedId, setTrackingEncryptedId] = useState(null);
  const [slow, setSlow] = useState(false);
  const [forcedOpen, setForcedOpen] = useState(false);

  // An admin can force the restaurant open outside the normal hours
  useEffect(() => {
    apiFetch("restaurant-status")
      .then((res) => setForcedOpen(Number(res?.is_open) === 2))
      .catch(() => {});
  }, [apiFetch]);

  const params = new URLSearchParams(location.search);
  const encryptedOrderId = params.get("order");

  useEffect(() => {
    if (!encryptedOrderId) {
      navigate("/", { replace: true });
      return undefined;
    }

    let cancelled = false;
    let attempts = 0;
    let timer = null;

    // The server decides: it only says "paid" once Stripe has confirmed the money.
    const check = async () => {
      attempts += 1;
      try {
        const result = await apiFetch("check-payment", {
          encrypted_order_id: encryptedOrderId,
        });
        if (cancelled) return;

        if (result?.paid) {
          setOrderId(result.order_id || null);
          setTotal(typeof result.total === "number" ? result.total : null);
          setTrackingEncryptedId(result.tracking_encrypted_order_id || null);
          setState("paid");
          clearCart();
          return;
        }
        if (result?.state === "failed") {
          setState("failed");
          return;
        }
      } catch (err) {
        console.error("Network error:", err);
      }

      if (attempts >= 15) setSlow(true);
      timer = setTimeout(check, attempts < 15 ? 2000 : 5000);
    };

    check();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [encryptedOrderId, apiFetch, clearCart, navigate]);

  const firstName = user?.first_name ? user.first_name.split(" ")[0] : "";
  // Same rules as the other checkout success screen: the site's opening schedule,
  // unless an admin has forced the restaurant open.
  const ukHour = Number(
    new Intl.DateTimeFormat("en-GB", { timeZone: "Europe/London", hour: "2-digit", hourCycle: "h23" }).format(new Date()),
  );
  const closedNow = !forcedOpen && !checkStoreStatus().isTakeawayOpen;
  const beforeOpening = closedNow && ukHour < 13;
  const afterClosing = closedNow && ukHour >= 13;
  const pageBase = "min-h-screen bg-black text-white px-[5%] pb-16 max-w-[460px] mt-[140px] md:mt-[170px] mx-auto font-poppins";

  return (
    <div className={pageBase}>
      {state === "checking" && (
        <div className="flex flex-col items-center justify-center gap-4 py-24 text-center">
          <div className="h-10 w-10 animate-spin rounded-full border-[3px] border-white/15 border-t-brand-orange" />
          <p className="font-prata text-xl font-bold">Confirming your payment…</p>
          <p className="max-w-[300px] text-sm text-white/60">
            Please don't close this page. This usually takes a few seconds.
          </p>
          {slow && (
            <p className="max-w-[320px] text-sm text-white/50">
              It's taking longer than usual. If you were charged, your order will appear in your order
              history shortly. You can also message us on WhatsApp.
            </p>
          )}
        </div>
      )}

      {state === "failed" && (
        <div className={`${card} text-center`}>
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-[#ff5252]/10 text-2xl text-[#ff5252] ring-2 ring-[#ff5252]/60">
            <i className="fas fa-xmark" />
          </div>
          <h1 className="mt-5 font-prata text-2xl font-bold">Payment not completed</h1>
          <p className="mx-auto mt-2 max-w-[320px] text-sm text-white/60">
            We didn't receive a payment for this order, so it hasn't been sent to the kitchen. You haven't
            been charged.
          </p>
          <div className="mt-6 space-y-3">
            <Link to="/order-online" className={primaryBtn}>
              Back to the menu
            </Link>
          </div>
        </div>
      )}

      {state === "paid" && (
        <>
          <div className={`${card} text-center`}>
            <div className="relative mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-brand-orange/15 text-2xl text-brand-orange ring-2 ring-brand-orange">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-brand-orange/25" style={{ animationIterationCount: 2 }} />
              <i className="fas fa-check relative" />
            </div>

            <h1 className="mt-5 font-prata text-2xl font-bold">Payment successful</h1>
            <p className="mx-auto mt-2 max-w-[320px] text-sm text-white/60">
              {firstName ? `Thank you, ${firstName}. ` : "Thank you. "}
              {beforeOpening
                ? "We'll start preparing your order when we open at 1 PM."
                : afterClosing
                  ? "We're closed for the night. We'll start preparing your order when we open at 1 PM tomorrow."
                  : "Your order is being processed."}
            </p>

            {(orderId || total !== null) && (
              <div className="mt-5 divide-y divide-white/[0.07] rounded-2xl border border-white/10 text-left text-sm">
                {orderId && (
                  <div className="flex items-center justify-between px-4 py-3">
                    <span className="text-white/50">Order ID</span>
                    <span className="font-semibold">L-{orderId}</span>
                  </div>
                )}
                {total !== null && (
                  <div className="flex items-center justify-between px-4 py-3">
                    <span className="text-white/50">Amount paid</span>
                    <span className="font-semibold">£{total.toFixed(2)}</span>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="mt-5 space-y-3">
            {trackingEncryptedId && (
              <Link to={`/order-tracking?id=${trackingEncryptedId}`} className={primaryBtn}>
                <i className="fas fa-location-dot" /> Track your order
              </Link>
            )}
            <Link to="/order-online" className={secondaryBtn}>
              Back to the menu
            </Link>
          </div>
        </>
      )}
    </div>
  );
}

export default PaymentSuccess;
